/**
 * Asset loader module
 *
 * This module:
 * - Provides methods for loading assets with caching
 * - Handles preloading of assets
 * - Manages cache invalidation
 */
import {
  AssetLoaderConfig,
  AssetType,
  CacheEntry,
  CacheOptions,
  CacheResult,
  ServiceWorkerMessageType,
} from "../config/types";
import {
  DEFAULT_CACHE_NAME,
  DEFAULT_CACHE_TTL,
  MAX_CACHEABLE_SIZE,
} from "../config/constants";
import {
  createCacheEntry,
  debugLog,
  hasExpired,
  shouldCacheAsset,
} from "../utils/cache-helpers";
import { generateCacheKey, generateContentHash } from "../utils/hash-generator";
import { CacheManager, CacheManagerState } from "./cache-manager";
import { VersionManager } from "./version-manager";
import { isServiceWorkerActive, sendMessageToSW } from "./register-sw";
import {
  CacheError,
  CacheErrorCode,
  CacheNotInitializedError,
  AssetFetchFailedError,
  AssetTooLargeError,
  AssetNotCachedError,
  AssetExpiredError,
  wrapError,
  isQuotaExceededError,
  StorageQuotaExceededError,
} from "./errors";

/**
 * Asset loader state
 */
export enum AssetLoaderState {
  UNINITIALIZED = "UNINITIALIZED",
  INITIALIZING = "INITIALIZING",
  READY = "READY",
  ERROR = "ERROR",
  CACHE_UNAVAILABLE = "CACHE_UNAVAILABLE",
}

/**
 * Asset loader initialization result
 */
export interface AssetLoaderInitResult {
  success: boolean;
  state: AssetLoaderState;
  cacheManagerState: CacheManagerState;
  error?: CacheError;
}

/**
 * Asset loader class
 */
export class AssetLoader {
  private static state: AssetLoaderState = AssetLoaderState.UNINITIALIZED;
  private static lastError: CacheError | null = null;
  private static initPromise: Promise<AssetLoaderInitResult> | null = null;
  private static config: AssetLoaderConfig = {
    cacheName: DEFAULT_CACHE_NAME,
    defaultTTL: DEFAULT_CACHE_TTL,
    maxSize: MAX_CACHEABLE_SIZE,
    enableServiceWorker: true,
    debug: false,
  };

  /**
   * Get the current state of the asset loader
   */
  static getState(): AssetLoaderState {
    return this.state;
  }

  /**
   * Get the last error that occurred
   */
  static getLastError(): CacheError | null {
    return this.lastError;
  }

  /**
   * Check if the asset loader is ready
   */
  static isReady(): boolean {
    return this.state === AssetLoaderState.READY;
  }

  /**
   * Check if caching is available (even if degraded)
   */
  static isCacheAvailable(): boolean {
    return (
      this.state === AssetLoaderState.READY ||
      CacheManager.getState() === CacheManagerState.READY
    );
  }

  /**
   * Initialize the asset loader
   * @param config Configuration options
   * @returns Initialization result with state information
   */
  static async init(
    config?: Partial<AssetLoaderConfig>
  ): Promise<AssetLoaderInitResult> {
    // Return existing promise if already initializing
    if (this.initPromise) {
      return this.initPromise;
    }

    // Already initialized - return current state
    if (this.state === AssetLoaderState.READY) {
      return {
        success: true,
        state: this.state,
        cacheManagerState: CacheManager.getState(),
      };
    }

    // Cache unavailable - don't retry
    if (this.state === AssetLoaderState.CACHE_UNAVAILABLE) {
      return {
        success: false,
        state: this.state,
        cacheManagerState: CacheManager.getState(),
        error: this.lastError || undefined,
      };
    }

    this.initPromise = this.performInit(config);
    return this.initPromise;
  }

  /**
   * Perform the actual initialization
   */
  private static async performInit(
    config?: Partial<AssetLoaderConfig>
  ): Promise<AssetLoaderInitResult> {
    this.state = AssetLoaderState.INITIALIZING;

    try {
      // Merge config with defaults
      this.config = {
        ...this.config,
        ...config,
      };

      debugLog("Initializing asset loader with config:", this.config);

      // Initialize the cache manager
      const cacheResult = await CacheManager.init(this.config.cacheName);

      if (!cacheResult.success) {
        // CacheManager failed to initialize
        if (cacheResult.state === CacheManagerState.UNSUPPORTED) {
          this.state = AssetLoaderState.CACHE_UNAVAILABLE;
          this.lastError = cacheResult.error || null;
          debugLog(
            "Asset loader: Cache API not supported, caching disabled",
            cacheResult.error
          );
        } else {
          this.state = AssetLoaderState.ERROR;
          this.lastError = cacheResult.error || null;
          debugLog(
            "Asset loader: CacheManager initialization failed",
            cacheResult.error
          );
        }

        return {
          success: false,
          state: this.state,
          cacheManagerState: cacheResult.state,
          error: cacheResult.error,
        };
      }

      this.state = AssetLoaderState.READY;
      this.lastError = null;
      debugLog("Asset loader initialized successfully");

      return {
        success: true,
        state: this.state,
        cacheManagerState: cacheResult.state,
      };
    } catch (error) {
      const cacheError = wrapError(error, "AssetLoader initialization failed");
      this.state = AssetLoaderState.ERROR;
      this.lastError = cacheError;
      debugLog("Asset loader initialization error:", cacheError);

      return {
        success: false,
        state: this.state,
        cacheManagerState: CacheManager.getState(),
        error: cacheError,
      };
    } finally {
      this.initPromise = null;
    }
  }

  /**
   * Reset the asset loader state (primarily for testing)
   */
  static reset(): void {
    this.state = AssetLoaderState.UNINITIALIZED;
    this.lastError = null;
    this.initPromise = null;
    this.config = {
      cacheName: DEFAULT_CACHE_NAME,
      defaultTTL: DEFAULT_CACHE_TTL,
      maxSize: MAX_CACHEABLE_SIZE,
      enableServiceWorker: true,
      debug: false,
    };
    CacheManager.reset();
  }

  /**
   * Attempt to recover from an error state
   * @param config Configuration options
   * @returns Recovery result
   */
  static async recover(
    config?: Partial<AssetLoaderConfig>
  ): Promise<AssetLoaderInitResult> {
    // Can't recover from CACHE_UNAVAILABLE - browser doesn't support Cache API
    if (this.state === AssetLoaderState.CACHE_UNAVAILABLE) {
      return {
        success: false,
        state: this.state,
        cacheManagerState: CacheManager.getState(),
        error: this.lastError || undefined,
      };
    }

    // Try to recover CacheManager first
    const cacheRecovery = await CacheManager.recover(this.config.cacheName);
    if (!cacheRecovery.success) {
      this.state = AssetLoaderState.ERROR;
      this.lastError = cacheRecovery.error || null;
      return {
        success: false,
        state: this.state,
        cacheManagerState: cacheRecovery.state,
        error: cacheRecovery.error,
      };
    }

    // Reset state and re-initialize
    this.state = AssetLoaderState.UNINITIALIZED;
    this.lastError = null;
    this.initPromise = null;

    return this.init(config);
  }

  /**
   * Ensure the asset loader is initialized
   * @throws {CacheNotInitializedError} If initialization fails
   */
  private static async ensureInitialized(): Promise<void> {
    if (this.state === AssetLoaderState.READY) {
      return;
    }

    if (this.state === AssetLoaderState.CACHE_UNAVAILABLE) {
      throw new CacheNotInitializedError(
        "Cache API not supported in this browser"
      );
    }

    const result = await this.init();
    if (!result.success) {
      throw (
        result.error ||
        new CacheNotInitializedError("AssetLoader initialization failed")
      );
    }
  }

  /**
   * Preload an asset
   * @param url URL of the asset to preload
   * @param options Caching options
   * @throws {CacheError} If preloading fails
   */
  static async preload(url: string, options?: CacheOptions): Promise<void> {
    await this.ensureInitialized();

    try {
      debugLog(`Preloading asset: ${url}`);

      // Check if already cached
      const isCached = await this.isCached(url);
      if (isCached) {
        debugLog(`Asset already cached: ${url}`);
        return;
      }

      // Fetch the asset
      let response: Response;
      try {
        response = await fetch(url);
      } catch (fetchError) {
        throw new AssetFetchFailedError(
          url,
          undefined,
          undefined,
          fetchError instanceof Error ? fetchError : undefined
        );
      }

      if (!response.ok) {
        throw new AssetFetchFailedError(url, response.status, response.statusText);
      }

      // Get the content type and size
      const contentType = response.headers.get("content-type");
      const contentLength = response.headers.get("content-length");
      const size = contentLength ? parseInt(contentLength, 10) : 0;

      // Check if the asset is too large
      const maxSize = this.config.maxSize ?? MAX_CACHEABLE_SIZE;
      if (size > 0 && size > maxSize) {
        throw new AssetTooLargeError(url, size, maxSize);
      }

      // Check if the asset should be cached
      if (
        !shouldCacheAsset(
          url,
          size,
          contentType || undefined,
          this.config.maxSize
        )
      ) {
        debugLog(`Asset not suitable for caching: ${url}`);
        return;
      }

      // Clone the response to avoid consuming it
      const responseClone = response.clone();

      // Get the asset data as a blob
      const blob = await responseClone.blob();

      // Generate a content hash for versioning
      const contentHash = await generateContentHash(blob);
      const version = options?.tags?.length
        ? `${contentHash}-${options.tags.join("-")}`
        : contentHash;

      // Create a cache entry
      const entry = createCacheEntry<Blob>(
        blob,
        url,
        contentType || undefined,
        {
          ttl: options?.ttl || this.config.defaultTTL,
          tags: options?.tags,
          version,
        }
      );

      // Store in the cache
      await this.storeInCache(url, entry);

      // If service worker is active, cache in the service worker too
      if (this.config.enableServiceWorker && isServiceWorkerActive()) {
        await sendMessageToSW(ServiceWorkerMessageType.CACHE_ASSET, {
          url,
          version,
          tags: options?.tags,
        });
      }

      debugLog(`Asset preloaded: ${url}`);
    } catch (error) {
      debugLog(`Failed to preload asset: ${url}`, error);
      // Re-throw CacheErrors as-is, wrap others
      if (error instanceof CacheError) {
        throw error;
      }
      throw wrapError(error, `Failed to preload asset: ${url}`);
    }
  }

  /**
   * Load an asset with caching
   * @param url URL of the asset to load
   * @param options Caching options
   * @returns Promise resolving to the asset data with typed error information
   */
  static async load<T = Blob>(
    url: string,
    options?: CacheOptions
  ): Promise<CacheResult<T>> {
    // Try to initialize if not ready
    if (this.state !== AssetLoaderState.READY) {
      const initResult = await this.init();
      if (!initResult.success) {
        // Return error result if initialization fails
        return {
          success: false,
          fromCache: false,
          error:
            initResult.error ||
            new CacheNotInitializedError("AssetLoader not initialized"),
        };
      }
    }

    try {
      debugLog(`Loading asset: ${url}`);

      // Try to get from cache first
      const cachedEntry = await this.getFromCache<T>(url);

      if (cachedEntry) {
        // Check if expired
        if (hasExpired(cachedEntry)) {
          debugLog(`Cached asset expired: ${url}`);
          // Remove from cache
          await this.removeFromCache(url);
        } else {
          debugLog(`Asset loaded from cache: ${url}`);
          // If revalidate is enabled, update the cache in the background
          if (options?.revalidate) {
            this.revalidateInBackground(url, options);
          }
          return {
            success: true,
            data: cachedEntry.data,
            fromCache: true,
          };
        }
      }

      // Not in cache or expired, fetch from network
      debugLog(`Fetching asset from network: ${url}`);

      // Create a versioned URL if needed
      const versionedUrl = VersionManager.hasVersion(url)
        ? url
        : VersionManager.createVersionedUrl(url);

      // Fetch the asset
      let response: Response;
      try {
        response = await fetch(versionedUrl);
      } catch (fetchError) {
        const error = new AssetFetchFailedError(
          url,
          undefined,
          undefined,
          fetchError instanceof Error ? fetchError : undefined
        );
        return {
          success: false,
          fromCache: false,
          error,
        };
      }

      if (!response.ok) {
        const error = new AssetFetchFailedError(
          url,
          response.status,
          response.statusText
        );
        return {
          success: false,
          fromCache: false,
          error,
        };
      }

      // Get the content type and size
      const contentType = response.headers.get("content-type");
      const contentLength = response.headers.get("content-length");
      const size = contentLength ? parseInt(contentLength, 10) : 0;

      // Get the asset data
      const data = (await response.blob()) as unknown as T;

      // Check if the asset should be cached
      if (
        shouldCacheAsset(
          url,
          size,
          contentType || undefined,
          this.config.maxSize
        )
      ) {
        try {
          // Generate a content hash for versioning
          const contentHash = await generateContentHash(
            data as unknown as Blob
          );
          const version = options?.tags?.length
            ? `${contentHash}-${options.tags.join("-")}`
            : contentHash;

          // Create a cache entry
          const entry = createCacheEntry<T>(
            data,
            url,
            contentType || undefined,
            {
              ttl: options?.ttl || this.config.defaultTTL,
              tags: options?.tags,
              version,
            }
          );

          // Store in the cache
          await this.storeInCache(url, entry);

          // If service worker is active, cache in the service worker too
          if (this.config.enableServiceWorker && isServiceWorkerActive()) {
            await sendMessageToSW(ServiceWorkerMessageType.CACHE_ASSET, {
              url,
              version,
              tags: options?.tags,
            });
          }
        } catch (cacheError) {
          // Log cache errors but don't fail the load - we still have the data
          debugLog(`Failed to cache asset (non-fatal): ${url}`, cacheError);
          // If quota exceeded, clear old entries and try again
          if (isQuotaExceededError(cacheError)) {
            debugLog("Storage quota exceeded, attempting cleanup...");
            // Don't throw - we have the data, just couldn't cache it
          }
        }
      }

      return {
        success: true,
        data,
        fromCache: false,
      };
    } catch (error) {
      debugLog(`Failed to load asset: ${url}`, error);
      const cacheError =
        error instanceof CacheError
          ? error
          : wrapError(error, `Failed to load asset: ${url}`);
      return {
        success: false,
        fromCache: false,
        error: cacheError,
      };
    }
  }

  /**
   * Check if an asset is cached
   * @param url URL of the asset
   * @returns Whether the asset is cached
   */
  static async isCached(url: string): Promise<boolean> {
    await this.ensureInitialized();

    try {
      // Check in-memory cache
      const cachedEntry = await this.getFromCache(url);
      if (cachedEntry && !hasExpired(cachedEntry)) {
        return true;
      }

      // If service worker is active, check service worker cache
      if (this.config.enableServiceWorker && isServiceWorkerActive()) {
        const result = await sendMessageToSW<{ cached: boolean }>(
          ServiceWorkerMessageType.CHECK_ASSET,
          { url }
        );
        return result?.cached || false;
      }

      return false;
    } catch (error) {
      debugLog(`Failed to check if asset is cached: ${url}`, error);
      return false;
    }
  }

  /**
   * Invalidate a cached asset
   * @param url URL of the asset to invalidate
   * @throws {CacheError} If invalidation fails
   */
  static async invalidate(url: string): Promise<void> {
    await this.ensureInitialized();

    try {
      debugLog(`Invalidating asset: ${url}`);

      // Remove from cache
      await this.removeFromCache(url);

      // If service worker is active, invalidate in the service worker too
      if (this.config.enableServiceWorker && isServiceWorkerActive()) {
        await sendMessageToSW(ServiceWorkerMessageType.INVALIDATE_ASSET, {
          url,
        });
      }

      debugLog(`Asset invalidated: ${url}`);
    } catch (error) {
      debugLog(`Failed to invalidate asset: ${url}`, error);
      if (error instanceof CacheError) {
        throw error;
      }
      throw wrapError(error, `Failed to invalidate asset: ${url}`);
    }
  }

  /**
   * Invalidate cached assets by tag
   * @param tag Tag to invalidate
   * @throws {CacheError} If invalidation fails
   */
  static async invalidateByTag(tag: string): Promise<void> {
    await this.ensureInitialized();

    try {
      debugLog(`Invalidating assets with tag: ${tag}`);

      // Get assets with the tag
      const assets = await CacheManager.getAssetsByTag(tag);

      // Invalidate each asset
      for (const asset of assets) {
        await this.removeFromCache(asset.url);
      }

      // If service worker is active, invalidate in the service worker too
      if (this.config.enableServiceWorker && isServiceWorkerActive()) {
        await sendMessageToSW(ServiceWorkerMessageType.INVALIDATE_TAG, {
          tag,
        });
      }

      debugLog(`Assets with tag invalidated: ${tag}`);
    } catch (error) {
      debugLog(`Failed to invalidate assets with tag: ${tag}`, error);
      if (error instanceof CacheError) {
        throw error;
      }
      throw wrapError(error, `Failed to invalidate assets with tag: ${tag}`);
    }
  }

  /**
   * Store an asset in the cache
   * @param url URL of the asset
   * @param entry Cache entry
   * @throws {CacheError} If storage fails (e.g., quota exceeded)
   */
  private static async storeInCache<T>(
    url: string,
    entry: CacheEntry<T>
  ): Promise<void> {
    try {
      // Generate a cache key
      const cacheKey = generateCacheKey(url);

      // Create a response from the blob
      const response = new Response(entry.data as unknown as Blob, {
        headers: {
          "Content-Type":
            entry.type === AssetType.OTHER
              ? "application/octet-stream"
              : `${entry.type}/*`,
        },
      });

      // Get the cache
      const cache = await caches.open(
        this.config.cacheName || DEFAULT_CACHE_NAME
      );

      // Store in the cache
      await cache.put(cacheKey, response);

      // Add to the manifest
      await CacheManager.addToManifest(url, {
        type: entry.type,
        version: entry.version || Date.now().toString(36),
        lastModified: new Date().toISOString(),
        size: entry.data instanceof Blob ? entry.data.size : 0,
        tags: entry.tags,
      });

      debugLog(`Asset stored in cache: ${url}`);
    } catch (error) {
      debugLog(`Failed to store asset in cache: ${url}`, error);
      // Check for quota exceeded error
      if (isQuotaExceededError(error)) {
        throw new StorageQuotaExceededError(
          error instanceof Error ? error : undefined
        );
      }
      if (error instanceof CacheError) {
        throw error;
      }
      throw wrapError(error, `Failed to store asset in cache: ${url}`);
    }
  }

  /**
   * Get an asset from the cache
   * @param url URL of the asset
   * @returns Cache entry or null if not found
   */
  private static async getFromCache<T>(
    url: string
  ): Promise<CacheEntry<T> | null> {
    try {
      // Generate a cache key
      const cacheKey = generateCacheKey(url);

      // Get the cache
      const cache = await caches.open(
        this.config.cacheName || DEFAULT_CACHE_NAME
      );

      // Try to get from cache
      const response = await cache.match(cacheKey);
      if (!response) {
        return null;
      }

      // Get the asset metadata
      const metadata = await CacheManager.getAssetMetadata(url);
      if (!metadata) {
        return null;
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create a cache entry
      return {
        data: blob as unknown as T,
        timestamp: new Date(metadata.lastModified).getTime(),
        expires: metadata.lastModified
          ? new Date(metadata.lastModified).getTime() +
            (this.config.defaultTTL || DEFAULT_CACHE_TTL) * 1000
          : undefined,
        type: metadata.type,
        tags: metadata.tags,
        version: metadata.version,
      };
    } catch (error) {
      debugLog(`Failed to get asset from cache: ${url}`, error);
      return null;
    }
  }

  /**
   * Remove an asset from the cache
   * @param url URL of the asset
   * @throws {CacheError} If removal fails
   */
  private static async removeFromCache(url: string): Promise<void> {
    try {
      // Generate a cache key
      const cacheKey = generateCacheKey(url);

      // Get the cache
      const cache = await caches.open(
        this.config.cacheName || DEFAULT_CACHE_NAME
      );

      // Remove from cache
      await cache.delete(cacheKey);

      // Remove from manifest
      await CacheManager.removeFromManifest(url);

      debugLog(`Asset removed from cache: ${url}`);
    } catch (error) {
      debugLog(`Failed to remove asset from cache: ${url}`, error);
      if (error instanceof CacheError) {
        throw error;
      }
      throw wrapError(error, `Failed to remove asset from cache: ${url}`);
    }
  }

  /**
   * Revalidate an asset in the background
   * Background revalidation errors are logged but not thrown
   * @param url URL of the asset
   * @param options Caching options
   */
  private static async revalidateInBackground(
    url: string,
    options?: CacheOptions
  ): Promise<void> {
    try {
      debugLog(`Revalidating asset in background: ${url}`);

      // Create a versioned URL if needed
      const versionedUrl = VersionManager.hasVersion(url)
        ? url
        : VersionManager.createVersionedUrl(url || "");

      // Fetch the asset
      let response: Response;
      try {
        response = await fetch(versionedUrl);
      } catch (fetchError) {
        debugLog(
          `Background revalidation fetch failed: ${url}`,
          fetchError
        );
        return; // Don't throw in background operation
      }

      if (!response.ok) {
        debugLog(
          `Background revalidation received non-ok response: ${url} (${response.status})`
        );
        return; // Don't throw in background operation
      }

      // Get the content type and size
      const contentType = response.headers.get("content-type");
      const contentLength = response.headers.get("content-length") || undefined;
      const size = contentLength ? parseInt(contentLength, 10) : 0;

      // Check if the asset should be cached
      if (
        !shouldCacheAsset(
          url,
          size,
          contentType || undefined,
          this.config.maxSize
        )
      ) {
        debugLog(`Asset not suitable for caching: ${url}`);
        return;
      }

      // Get the asset data
      const blob = await response.blob();

      // Generate a content hash for versioning
      const contentHash = await generateContentHash(blob);
      const version = options?.tags?.length
        ? `${contentHash}-${options.tags.join("-")}`
        : contentHash;

      // Get the current version from the cache
      const metadata = await CacheManager.getAssetMetadata(url);
      if (metadata && metadata.version === version) {
        debugLog(`Asset unchanged, no need to update cache: ${url}`);
        return;
      }

      // Create a cache entry
      const entry = createCacheEntry<Blob>(
        blob,
        url,
        contentType || undefined,
        {
          ttl: options?.ttl || this.config.defaultTTL,
          tags: options?.tags,
          version,
        }
      );

      // Store in the cache
      await this.storeInCache(url, entry);

      // If service worker is active, cache in the service worker too
      if (this.config.enableServiceWorker && isServiceWorkerActive()) {
        await sendMessageToSW(ServiceWorkerMessageType.CACHE_ASSET, {
          url,
          version,
          tags: options?.tags,
        });
      }

      debugLog(`Asset revalidated: ${url}`);
    } catch (error) {
      // Log but don't throw - this is a background operation
      debugLog(`Failed to revalidate asset (background): ${url}`, error);
    }
  }
}
